import { flatMap } from '@execonline-inc/collections';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
  SelectionOption,
  isReadonly,
  isRequired as isRequiredByMode,
  SelectionsField,
  OptionsListOrRelOrBoth,
} from '../../Types';
import EXOInputErrorMessage from '../LegacyDependencies/EXOInputErrorMessage';
import SyncOrAsync from './SyncOrAsync';
import { SingleValue, MultiValue } from 'react-select';
import { Maybe } from 'maybeasy';

interface Props {
  field: SelectionsField;
  dataIntercomTarget?: string;
}

export type Value = SelectionsField['value'];

const onChange = (field: SelectionsField) =>
  action((value: Value) => {
    field.value = value;
  });

function isMultiValue<T>(arg: MultiValue<T> | SingleValue<T>): arg is MultiValue<T> {
  return Array.isArray(arg);
}

const optionToValue = (
  option: SingleValue<SelectionOption> | MultiValue<SelectionOption>,
): Value => {
  if (option == null) {
    return [];
  } else if (isMultiValue(option)) {
    return flatMap((o) => (o ? [o] : []), option);
  } else {
    return option;
  }
};

export const prepOnChange =
  (fn: (value: Value) => void) =>
  (e: SingleValue<SelectionOption> | MultiValue<SelectionOption>): void => {
    fn(optionToValue(e));
  };

export const placeholder = (field: SelectionsField): string => {
  if (isReadonly(field)) {
    return '(Readonly)';
  } else if (field.kind === 'single-selection') {
    return `Pick one`;
  } else if (field.minSelections <= 1) {
    return `Pick up to ${field.maxSelections}`;
  } else if (field.minSelections === field.maxSelections) {
    return `Pick exactly ${field.minSelections}`;
  } else {
    return `Pick from ${field.minSelections} to ${field.maxSelections}`;
  }
};

type BackgroundColor = '#187A90' | '#187a904d';

export const backgroundColor = (
  isFocused: boolean,
  isSelected: boolean,
): BackgroundColor | undefined => {
  if (isSelected) {
    return '#187A90';
  } else if (isFocused) {
    return '#187a904d';
  }

  return undefined;
};

export const findInOptions = (value: string, options: OptionsListOrRelOrBoth) => {
  switch (options.kind) {
    case 'options-list':
    case 'options-list-and-rel':
      return options.options.find((element) => element.value === value) || null;
    case 'options-rel':
      return null;
  }
};

export const multiValueOptions = (
  value: readonly string[],
  options: OptionsListOrRelOrBoth,
): MultiValue<SelectionOption> => {
  switch (options.kind) {
    case 'options-list':
    case 'options-list-and-rel':
      return options.options.filter((element) => value.includes(element.value));
    case 'options-rel':
      return [];
  }
};

export const isRequired = (field: SelectionsField): boolean =>
  isRequiredByMode(field) || field.minSelections > 0;

export const isMulti = (field: SelectionsField): boolean => field.maxSelections > 1;

export const singleValueOption = (
  value: Maybe<string>,
  options: OptionsListOrRelOrBoth,
): SingleValue<SelectionOption> => value.map((a) => findInOptions(a, options)).getOrElseValue(null);

const MultipleSelectionsInput: React.FC<Props> = ({ field, dataIntercomTarget }) => {
  return (
    <div>
      <SyncOrAsync
        field={field}
        onChange={onChange(field)}
        dataIntercomTarget={dataIntercomTarget}
      />
      {field.errors.map((err) => (
        <EXOInputErrorMessage key={err} message={err} />
      ))}
    </div>
  );
};

export default observer(MultipleSelectionsInput);
